import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  CheckOutlined,
  UpOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import styles from "./NotificationBanner.module.less";
import { Typography } from "antd";
import { IAlertMessage } from "~/types/alert";
import { useUserLogContext } from "~/providers/UserLogProvider";
import { CopilotLogsType } from "~/pages/copilotMessage/types/copilotLogs";

interface NotificationBannerProps {
  messages?: IAlertMessage[];
  showDetailsButton?: boolean;
  showAcknowledgeButton?: boolean;
  acknowledgeLoading?: boolean;
  onClickDetailsButton?: (
    alertId: string,
  ) => Promise<void> | ((alertId: string) => void);
  onAcknowledge?: (messageId: string) => Promise<void>;
}

const NotificationBanner: React.FC<NotificationBannerProps> = ({
  messages = [],
  showDetailsButton = false,
  showAcknowledgeButton = false,
  acknowledgeLoading = false,
  onClickDetailsButton,
  onAcknowledge,
}) => {
  const { insertUserLog } = useUserLogContext();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [detailsLoading, setDetailsLoading] = useState(false);

  //if has new message, expand the banner
  const [isExpanded, setIsExpanded] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const canPauseRef = useRef(true);

  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const messageTextRef = useRef<HTMLDivElement>(null);

  const SCROLL_ANIMATION_DURATION = 20000;
  const MESSAGE_CHANGE_INTERVAL = SCROLL_ANIMATION_DURATION + 1000;

  useEffect(() => {
    const startInterval = () => {
      if (messages.length > 1 && !isPaused) {
        intervalRef.current = setInterval(() => {
          canPauseRef.current = false;
          setIsChanging(true);

          timeoutRef.current = setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % messages.length);
            setIsChanging(false);
            canPauseRef.current = true;
          }, 500);
        }, MESSAGE_CHANGE_INTERVAL);
      }
    };

    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [messages.length, isPaused, MESSAGE_CHANGE_INTERVAL]);

  const handleMouseEnter = useCallback(() => {
    if (canPauseRef.current) {
      setIsPaused(true);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [canPauseRef, intervalRef, timeoutRef]);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleAcknowledge = useCallback(
    async (index: number) => {
      const currentMessage = messages[index];
      if (currentMessage?.id && onAcknowledge) {
        await onAcknowledge(currentMessage.id);
        setCurrentIndex(currentIndex - 1 < 0 ? 0 : currentIndex - 1);
        insertUserLog(CopilotLogsType.AlertAcknowledged);
      }
    },
    [currentIndex, messages, onAcknowledge, insertUserLog],
  );

  const handlePrevMessage = useCallback(() => {
    if (messageTextRef.current) {
      messageTextRef.current.classList.remove(styles.scrolling);
    }

    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));

    setTimeout(() => {
      if (messageTextRef.current) {
        messageTextRef.current.classList.add(styles.scrolling);
      }
    }, 100);
  }, []);

  const handleNextMessage = useCallback(() => {
    if (messageTextRef.current) {
      messageTextRef.current.classList.remove(styles.scrolling);
    }

    setCurrentIndex((prev) => (prev < messages.length - 1 ? prev + 1 : prev));

    setTimeout(() => {
      if (messageTextRef.current) {
        messageTextRef.current.classList.add(styles.scrolling);
      }
    }, 100);
  }, [messages.length]);

  const handleDetailsClick = useCallback(async () => {
    const currentAlert = messages[currentIndex];
    if (currentAlert) {
      setDetailsLoading(true);
      await onClickDetailsButton?.(currentAlert.id);
      setDetailsLoading(false);
      insertUserLog(CopilotLogsType.AlertDetailsClicked);
    }
  }, [currentIndex, onClickDetailsButton, messages, insertUserLog]);

  useEffect(() => {
    if (messages.length > 0) {
      setIsExpanded(true);
    }
  }, [messages.length]);

  useEffect(() => {
    const checkOverflow = () => {
      const element = messageTextRef.current;
      if (element) {
        setIsTextOverflowing(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [messages, currentIndex, isExpanded]);

  return messages.length === 0 ? null : (
    <div
      className={`${styles.banner} ${isExpanded ? styles.expanded : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isExpanded && (
        <div className={`${styles.expandButtonContainer}`}>
          <UpOutlined
            className={styles.navButton}
            onClick={() => {
              setIsExpanded(false);
              insertUserLog(CopilotLogsType.AlertCollapsed);
            }}
          />
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.mikeInfo}>
          <img
            src="/images/mike.opac.png"
            alt="Mike"
            className={styles.mikeIcon}
          />
          <div className={styles.titleWrapper}>
            <Typography.Title level={1} style={{ color: "white" }}>
              Ladle Copilot
            </Typography.Title>
            <Typography.Title level={4} style={{ color: "white" }}>
              Mike
            </Typography.Title>
          </div>
        </div>
      </div>

      <div
        className={`${styles.content} ${
          isTextOverflowing ? styles.scrolling : ""
        }`}
      >
        <div
          ref={messageTextRef}
          className={`${
            isChanging ? styles.messageTextExit : styles.messageText
          } ${!isExpanded && isTextOverflowing ? styles.scrolling : ""}`}
          title={
            !isExpanded ? messages?.[currentIndex]?.message.toString() : ""
          }
          onClick={() => {
            if (!isChanging && !isExpanded && handleDetailsClick) {
              handleDetailsClick();
            }
          }}
          style={{
            cursor: isChanging || isExpanded ? "default" : "pointer",
          }}
        >
          {detailsLoading && <LoadingOutlined />}
          {messages[currentIndex]?.message}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.controlsContainer}>
          <div className={styles.navigationContainer}>
            {messages.length > 1 && (
              <button
                className={styles.navButton}
                onClick={handlePrevMessage}
                aria-label="Previous message"
              >
                <LeftOutlined />
              </button>
            )}
            <span className={styles.counter}>{`${currentIndex + 1}/${
              messages.length
            }`}</span>
            {messages.length > 1 && (
              <button
                className={styles.navButton}
                onClick={handleNextMessage}
                aria-label="Next message"
              >
                <RightOutlined />
              </button>
            )}
          </div>

          <div className={styles.actionsContainer}>
            {showDetailsButton && (
              <button
                className={`${styles.detailsButton} ${
                  detailsLoading ? styles.disabled : ""
                }`}
                onClick={handleDetailsClick}
                disabled={detailsLoading}
              >
                Details
                {detailsLoading ? <LoadingOutlined /> : <ArrowRightOutlined />}
              </button>
            )}

            {showAcknowledgeButton && (
              <button
                className={`${styles.acknowledgeButton} ${
                  acknowledgeLoading ? styles.disabled : ""
                }`}
                onClick={() => handleAcknowledge(currentIndex)}
                disabled={acknowledgeLoading}
              >
                <span>Acknowledge</span>
                {acknowledgeLoading ? (
                  <LoadingOutlined />
                ) : (
                  <CheckOutlined className={styles.checkIcon} />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationBanner;
